import { X } from 'lucide-react';
import * as React from 'react';
import * as DialogPrimitive from '@radix-ui/react-dialog';

import { Button } from '@/ux-common/components/button/button';
import { cn } from '@/ux-common/lib/utils';

/**
 * Dialog — minimal Radix wrappers for a modal surface.
 *
 * The dialog is a themed surface with three stacked regions separated by a
 * uniform 20px gap: a header, a body, and a footer. Rather than ship bespoke
 * header markup, the registry dialog expects the consumer to compose a
 * registry `<Header>` inside `<DialogContent>` — exactly the arrangement the
 * Figma models, where only Body and Footer are slots. All header
 * look-and-feel continues to live in Header and its `text-header-*`
 * typography; the dialog contributes only the surface, the layout, and the
 * modal behavior Radix provides.
 *
 * `DialogTitle` and `DialogDescription` ship NO typography. They exist purely
 * to wire Radix's `aria-labelledby` / `aria-describedby`; pass `asChild` and
 * wrap a `<Typography>` so Typography keeps owning the visual style.
 *
 * **No motion.** The Figma specifies no enter or exit animation, so none is
 * shipped — deliberately unlike the legacy CKDS dialog's zoom-from-top-left.
 *
 * **Dismissal.** Escape and an outside (scrim) click both close the dialog. Pass
 * `dismissOnOutsideInteraction={false}` to `DialogContent` for a task whose
 * progress a stray click would discard — a wizard or a long form. Escape closes
 * it either way, so pair that with a visible way out: a `DialogCloseButton` in
 * `HeaderActions`, or a Cancel in the footer. A forced choice that interrupts the
 * user is an `AlertDialog` instead, which also announces as `role="alertdialog"`
 * and focuses its cancel action.
 *
 * **Class API** (owned by `theme-default`, restyled by `titan`):
 * - `.dialog-overlay` — the scrim
 * - `.dialog` — the surface (fixed, centred, capped to the viewport)
 * - `.dialog-sm` / `.dialog-md` / `.dialog-lg` — 400 / 600 / 1000 px
 * - `.dialog-body` — the only scroll region; header and footer stay pinned
 * - `.dialog-footer` — layout-only row; children keep natural width
 * - `.dialog-footer-buttons` — equal-width, full-span button row
 *
 * @example
 * ```tsx
 * <Dialog>
 *   <DialogTrigger asChild><Button>Open</Button></DialogTrigger>
 *   <DialogContent className="dialog-md">
 *     <Header>
 *       <HeaderTitle>
 *         <DialogTitle asChild>
 *           <Typography as="h2" className="text-header-section">Complex</Typography>
 *         </DialogTitle>
 *       </HeaderTitle>
 *       <HeaderActions><DialogCloseButton /></HeaderActions>
 *     </Header>
 *     <DialogBody>…</DialogBody>
 *     <DialogFooter>
 *       <DialogFooterButtons>
 *         <DialogClose asChild><Button className="btn-tier2">Back</Button></DialogClose>
 *         <Button className="btn-brand">Continue</Button>
 *       </DialogFooterButtons>
 *     </DialogFooter>
 *   </DialogContent>
 * </Dialog>
 * ```
 */
export const Dialog = DialogPrimitive.Root;

export const DialogTrigger = DialogPrimitive.Trigger;

export const DialogPortal = DialogPrimitive.Portal;

export const DialogClose = DialogPrimitive.Close;

export const DialogTitle = DialogPrimitive.Title;

export const DialogDescription = DialogPrimitive.Description;

/**
 * DialogOverlay — the scrim. Renders `DialogPrimitive.Overlay` with the
 * `.dialog-overlay` class auto-applied.
 */
export type DialogOverlayProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Overlay>;

export const DialogOverlay = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Overlay>,
  DialogOverlayProps
>(({ className, ...props }, ref) => (
  <DialogPrimitive.Overlay
    ref={ref}
    data-component="DialogOverlay"
    className={cn('dialog-overlay', className)}
    {...props}
  />
));
DialogOverlay.displayName = 'DialogOverlay';

/**
 * DialogContent — the surface. Renders the portal, the overlay, and
 * `DialogPrimitive.Content` with `.dialog` auto-applied. Apply a size via
 * `className` (`dialog-sm` / `dialog-md` / `dialog-lg`); with none, the
 * surface is fluid between its 320px min and 1000px max.
 */
export type DialogContentProps = React.ComponentPropsWithoutRef<typeof DialogPrimitive.Content> & {
  /** Class applied to the scrim rather than the surface. */
  overlayClassName?: string;
  /**
   * Whether a pointer press or focus move outside the surface closes the dialog.
   * Defaults to `true`.
   *
   * Set `false` for a user-initiated task whose progress a stray click would
   * discard — a multi-step wizard or a long form. Escape still closes the dialog
   * either way, so pair this with a visible way out: a `DialogCloseButton` in
   * `HeaderActions`, or a Cancel in the footer.
   */
  dismissOnOutsideInteraction?: boolean;
};

export const DialogContent = React.forwardRef<
  React.ElementRef<typeof DialogPrimitive.Content>,
  DialogContentProps
>(
  (
    {
      overlayClassName,
      className,
      children,
      dismissOnOutsideInteraction = true,
      onPointerDownOutside,
      onInteractOutside,
      ...props
    },
    ref
  ) => {
    /*
     * Both handlers are blocked, not just one. `onInteractOutside` covers focus
     * moving out as well as pointer presses, and Radix fires the more specific
     * `onPointerDownOutside` alongside it — preventing only one leaves the other
     * path open.
     *
     * The consumer's handler runs FIRST and `preventDefault()` follows regardless,
     * so a handler that does its own work cannot undo the opt-in by forgetting to
     * call it. Radix reads `defaultPrevented` after the event returns, so the order
     * is what makes this hold.
     *
     * `onEscapeKeyDown` is deliberately untouched: the WAI-ARIA dialog pattern
     * requires Escape to close, and a dialog that cannot be closed from the
     * keyboard is a trap. Escape is a deliberate act; an outside click is a miss.
     */
    const blockOutside = <E extends { preventDefault: () => void }>(
      consumerHandler: ((event: E) => void) | undefined
    ) =>
      dismissOnOutsideInteraction
        ? consumerHandler
        : (event: E) => {
            consumerHandler?.(event);
            event.preventDefault();
          };

    return (
      <DialogPortal>
        <DialogOverlay className={overlayClassName} />
        <DialogPrimitive.Content
          ref={ref}
          data-component="DialogContent"
          className={cn('dialog', className)}
          onPointerDownOutside={blockOutside(onPointerDownOutside)}
          onInteractOutside={blockOutside(onInteractOutside)}
          {...props}
        >
          {children}
        </DialogPrimitive.Content>
      </DialogPortal>
    );
  }
);
DialogContent.displayName = 'DialogContent';

/**
 * DialogBody — the scrolling content region. The only element in the dialog
 * that scrolls; the header and footer stay pinned when content overflows.
 */
export type DialogBodyProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogBody = React.forwardRef<HTMLDivElement, DialogBodyProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-component="DialogBody"
      className={cn('dialog-body', className)}
      {...props}
    />
  )
);
DialogBody.displayName = 'DialogBody';

/**
 * DialogFooter — layout-only action row. Children keep their natural width.
 * For the equal-width, full-span row the Figma shows, nest a
 * `<DialogFooterButtons>` inside.
 */
export type DialogFooterProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogFooter = React.forwardRef<HTMLDivElement, DialogFooterProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-component="DialogFooter"
      className={cn('dialog-footer', className)}
      {...props}
    />
  )
);
DialogFooter.displayName = 'DialogFooter';

/**
 * DialogFooterButtons — equal-width, full-span button row. Every direct child
 * gets `flex: 1 1 0`, matching the Figma sticker sheet at all three widths.
 */
export type DialogFooterButtonsProps = React.HTMLAttributes<HTMLDivElement>;

export const DialogFooterButtons = React.forwardRef<HTMLDivElement, DialogFooterButtonsProps>(
  ({ className, ...props }, ref) => (
    <div
      ref={ref}
      data-component="DialogFooterButtons"
      className={cn('dialog-footer-buttons', className)}
      {...props}
    />
  )
);
DialogFooterButtons.displayName = 'DialogFooterButtons';

/**
 * DialogCloseButton — the header close affordance. Renders a registry
 * `Button` through Radix's `Close` via `asChild`, so hover, focus-visible,
 * active, and disabled all come from Button rather than being re-implemented.
 * Place it inside `<HeaderActions>`; per UXD-31 it is the only thing that slot
 * holds.
 *
 * `btn-transparent` matches the Figma tokens (`Button/bg/transparent`,
 * `Button/fg/transparent`) exactly but is defined only in the Titan theme
 * today — see UXD-31 comment item 7.
 */
export type DialogCloseButtonProps = Omit<
  React.ComponentPropsWithoutRef<typeof Button>,
  'children'
>;

export const DialogCloseButton = React.forwardRef<HTMLButtonElement, DialogCloseButtonProps>(
  ({ className, 'aria-label': ariaLabel = 'Close', ...props }, ref) => (
    <DialogPrimitive.Close asChild>
      <Button
        ref={ref}
        aria-label={ariaLabel}
        data-component="DialogCloseButton"
        className={cn('btn-transparent btn-md btn-square', className)}
        {...props}
      >
        <X className="btn-icon" aria-hidden />
      </Button>
    </DialogPrimitive.Close>
  )
);
DialogCloseButton.displayName = 'DialogCloseButton';
